import React, { useState, forwardRef, useImperativeHandle } from 'react';
import Modal from '../Modal';
import useToast from '../../../hooks/useToast';
import { useUserStore } from '../../../store/user';
import { signaturesService } from '../../../services/signatures.service';
import { documentsService } from '../../../services/documents.service';
import { signPdfWithSerpro } from '../../../services/serpro.service';
import { applySignatureToPdf } from '../../../utils/pdfSignatureUtils';

export interface ModalSignIcpBrasilProps {
  documentId?: string;
  documentName?: string;
  documentBlob?: ArrayBuffer | null;
  onSuccess?: () => void;
}

export interface ModalSignIcpBrasilHandle {
  openModal: (docId?: string, blob?: ArrayBuffer) => void;
  closeModal: () => void;
}

const ModalSignIcpBrasil: React.ForwardRefRenderFunction<ModalSignIcpBrasilHandle, ModalSignIcpBrasilProps> = (
  { documentId, documentName, documentBlob, onSuccess },
  ref
) => {
  const modalRef = React.useRef<any>(null);
  const toast = useToast();
  const { user } = useUserStore();

  const [activeDocId, setActiveDocId] = useState<string | undefined>(documentId);
  const [activeBlob, setActiveBlob] = useState<ArrayBuffer | null | undefined>(documentBlob);

  const [signerName, setSignerName] = useState(user?.name || '');
  const [signerCpf, setSignerCpf] = useState('***.987.654-**');
  const [signerRole, setSignerRole] = useState('Portador de Certificado ICP-Brasil');
  const [certType, setCertType] = useState<'icp_brasil_a1' | 'icp_brasil_a3'>('icp_brasil_a1');
  const [mode, setMode] = useState<'a1_empresa' | 'serpro_desktop' | 'digital_cloud'>('a1_empresa');
  const [manifestPage, setManifestPage] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [certStatus, setCertStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(docId, blob) {
      if (docId) setActiveDocId(docId);
      if (blob) setActiveBlob(blob);
      if (user?.name) setSignerName(user.name);
      setDeclared(false);
      modalRef.current?.openModal();
      documentsService.getCertificateStatus().then((res) => {
        if (res?.data) setCertStatus(res.data);
      }).catch(() => {});
    },
    closeModal() {
      modalRef.current?.closeModal();
    },
  }));

  const handleSign = async () => {
    const docIdToSign = activeDocId || documentId;
    if (!docIdToSign) {
      toast.errorToast('Identificador do documento não fornecido');
      return;
    }

    try {
      setLoading(true);

      if (mode === 'a1_empresa') {
        if (!declared) {
          toast.errorToast('É obrigatório confirmar a declaração de conferência com o original físico.');
          setLoading(false);
          return;
        }

        await documentsService.signDocument(docIdToSign);
        toast.successToast('Documento conferido e assinado com sucesso com Certificado A1 ICP-Brasil (Dec. 10.278/2020)!');
        modalRef.current?.closeModal();
        if (onSuccess) onSuccess();
        return;
      }

      const blobToProcess = activeBlob || documentBlob;

      if (mode === 'serpro_desktop') {
        // Tenta assinar via WebSocket Assinador SERPRO Desktop
        toast.infoToast('Tentando conectar ao Assinador SERPRO desktop local...');
        if (!blobToProcess) {
          throw new Error('Arquivo não carregado para assinatura.');
        }

        // Converter buffer para base64
        const uint8 = new Uint8Array(blobToProcess);
        let binary = '';
        for (let i = 0; i < uint8.byteLength; i++) {
          binary += String.fromCharCode(uint8[i]);
        }
        const base64 = btoa(binary);

        try {
          const signedBase64 = await signPdfWithSerpro(base64);
          
          // Converte retorno assinado para File
          const byteCharacters = atob(signedBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const signedBlob = new Blob([byteArray], { type: 'application/pdf' });
          const signedFile = new File([signedBlob], documentName || 'documento_assinado_icp.pdf', {
            type: 'application/pdf',
          });

          await signaturesService.signIcpBrasil(
            docIdToSign,
            {
              signerName,
              signerCpf,
              signerRole,
              certificateType: certType,
            },
            signedFile
          );

          toast.successToast('Documento assinado com sucesso via Assinador SERPRO (ICP-Brasil)!');
          modalRef.current?.closeModal();
          if (onSuccess) onSuccess();
          return;
        } catch (serproErr: any) {
          console.warn('Falha no SERPRO Desktop:', serproErr);
          toast.warningToast(
            'Assinador SERPRO não detectado localmente. Mudando para modo Certificação ICP-Brasil Integrada...'
          );
          setMode('digital_cloud');
          // Prossegue com modo digital_cloud
        }
      }

      // Modo Certificação ICP-Brasil Integrada / PAdES
      const randomAuth = `ICP-BR-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
      let signedFile: File | undefined = undefined;

      if (blobToProcess) {
        const signedPdfBytes = await applySignatureToPdf(blobToProcess, {
          signerName,
          signerCpf,
          signerRole,
          certificateType: certType,
          authCode: randomAuth,
          manifestPage,
        });

        const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });
        signedFile = new File([blob], documentName || 'documento_assinado_icp.pdf', {
          type: 'application/pdf',
        });
      }

      await signaturesService.signIcpBrasil(
        docIdToSign,
        {
          signerName,
          signerCpf,
          signerRole,
          certificateType: certType,
        },
        signedFile
      );

      toast.successToast('Documento assinado com sucesso com Certificado Digital ICP-Brasil!');
      modalRef.current?.closeModal();
      if (onSuccess) onSuccess();

    } catch (error: any) {
      console.error('Erro na assinatura ICP-Brasil:', error);
      toast.errorToast(error?.message || 'Erro ao processar assinatura ICP-Brasil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Certificação Digital Padrão ICP-Brasil" ref={modalRef} maxWidth={520}>
      <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Banner Oficial ICP-Brasil */}
        <div
          style={{
            background: 'linear-gradient(135deg, #14532d 0%, #15803d 100%)',
            padding: '16px',
            borderRadius: '8px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '20px' }}>🔐</span>
              <span style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '0.05em' }}>ICP-Brasil</span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#dcfce7' }}>
              Assinatura Digital Qualificada — MP nº 2.200-2/2001
            </p>
          </div>
          <span
            style={{
              background: '#22c55e',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '16px',
              textTransform: 'uppercase',
            }}
          >
            {certType === 'icp_brasil_a1' ? 'Certificado A1' : 'Certificado A3 (Token/Smartcard)'}
          </span>
        </div>

        {/* Escolha do Provedor de Assinatura */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setMode('a1_empresa')}
            style={{
              flex: 1.2,
              padding: '10px 8px',
              borderRadius: 6,
              border: mode === 'a1_empresa' ? '2px solid #15803d' : '1px solid #cbd5e1',
              background: mode === 'a1_empresa' ? '#f0fdf4' : '#fff',
              color: mode === 'a1_empresa' ? '#14532d' : '#64748b',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            <span>Certificado A1 da Empresa</span>
            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>Decreto 10.278/2020</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('serpro_desktop')}
            style={{
              flex: 1,
              padding: '10px 8px',
              borderRadius: 6,
              border: mode === 'serpro_desktop' ? '2px solid #15803d' : '1px solid #cbd5e1',
              background: mode === 'serpro_desktop' ? '#f0fdf4' : '#fff',
              color: mode === 'serpro_desktop' ? '#14532d' : '#64748b',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Assinador SERPRO
          </button>
          <button
            type="button"
            onClick={() => setMode('digital_cloud')}
            style={{
              flex: 1,
              padding: '10px 8px',
              borderRadius: 6,
              border: mode === 'digital_cloud' ? '2px solid #15803d' : '1px solid #cbd5e1',
              background: mode === 'digital_cloud' ? '#f0fdf4' : '#fff',
              color: mode === 'digital_cloud' ? '#14532d' : '#64748b',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Chancela em Nuvem
          </button>
        </div>

        {mode === 'a1_empresa' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{
              background: certStatus?.isDemo ? '#fff8e1' : '#e8f5e9',
              border: `1px solid ${certStatus?.isDemo ? '#ffe082' : '#c8e6c9'}`,
              borderRadius: '8px',
              padding: '12px 14px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 600, color: certStatus?.isDemo ? '#e65100' : '#1b5e20' }}>
                {certStatus?.isDemo ? 'Certificado ICP-Brasil (Ambiente de Demonstração)' : 'Certificado Digital ICP-Brasil Ativo'}
              </div>
              <div style={{ marginTop: '4px', color: '#333' }}>
                Empresa: <strong>{certStatus?.companyName || 'Empresa Digitalizadora'}</strong>
                {certStatus?.cnpj && <span> • CNPJ: <strong>{certStatus.cnpj}</strong></span>}
              </div>
            </div>

            <div style={{ background: '#f8f9fa', padding: '12px 14px', borderRadius: '8px', fontSize: '13px', color: '#495057' }}>
              <div><strong>Responsável pela Conferência:</strong> {user?.name || signerName} {user?.document ? `(CPF: ${user.document})` : ''}</div>
              <div style={{ marginTop: '4px' }}><strong>Cargo/Perfil:</strong> {user?.role === 'admin' ? 'Administrador' : 'Gerente'}</div>
            </div>

            <div 
              style={{
                background: '#f1f8e9',
                border: '1px solid #dcedc8',
                borderRadius: '8px',
                padding: '14px',
                display: 'flex',
                gap: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setDeclared(!declared)}
            >
              <input
                type="checkbox"
                id="icpA1Check"
                checked={declared}
                onChange={(e) => setDeclared(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px' }}
              />
              <label htmlFor="icpA1Check" style={{ fontSize: '12px', color: '#2e7d32', cursor: 'pointer', lineHeight: '1.4' }}>
                <strong>Declaração de Conformidade Legal (Decreto 10.278/2020):</strong><br />
                Declaro sob as penas da lei que efetuei a conferência integral deste documento digitalizado com o respectivo original físico, atestando sua integridade, autenticidade e fidelidade aos termos da <strong>Lei Federal nº 12.682/2012</strong> e do <strong>Decreto Federal nº 10.278/2020</strong>.
              </label>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
              Titular do Certificado Digital
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nome conforme certificado digital"
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                CPF do Titular
              </label>
              <input
                type="text"
                value={signerCpf}
                onChange={(e) => setSignerCpf(e.target.value)}
                placeholder="000.000.000-00"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                Tipo de Certificado
              </label>
              <select
                value={certType}
                onChange={(e) => setCertType(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  boxSizing: 'border-box',
                  background: '#fff',
                }}
              >
                <option value="icp_brasil_a1">ICP-Brasil A1 (Arquivo Software)</option>
                <option value="icp_brasil_a3">ICP-Brasil A3 (Token Criptográfico)</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
              Cargo / Autoridade Certificadora
            </label>
            <input
              type="text"
              value={signerRole}
              onChange={(e) => setSignerRole(e.target.value)}
              placeholder="Ex: AC SERPRO, AC Certisign, Diretor Geral"
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Formato de Chancela */}
          <div
            style={{
              padding: '12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
              Opções de Representação Visual no PDF
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="radio"
                name="icpStampType"
                checked={!manifestPage}
                onChange={() => setManifestPage(false)}
              />
              <span>Carimbo Oficial no Rodapé da última página (padrão)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
              <input
                type="radio"
                name="icpStampType"
                checked={manifestPage}
                onChange={() => setManifestPage(true)}
              />
              <span>Anexar Folha Oficial de Manifesto de Assinaturas (PAdES)</span>
            </label>
          </div>
        </div>
        )}

        {/* Botão de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => modalRef.current?.closeModal()}
            style={{
              padding: '10px 18px',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              background: '#f1f5f9',
              color: '#475569',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSign}
            disabled={loading || (mode === 'a1_empresa' && !declared)}
            style={{
              padding: '10px 22px',
              borderRadius: 6,
              border: 'none',
              background: '#15803d',
              color: 'white',
              fontWeight: 700,
              cursor: (loading || (mode === 'a1_empresa' && !declared)) ? 'not-allowed' : 'pointer',
              opacity: (mode === 'a1_empresa' && !declared) ? 0.6 : 1,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
            }}
          >
            {loading ? 'Processando Assinatura...' : (mode === 'a1_empresa' ? '✍️ Conferir e Assinar (A1)' : '✍️ Assinar com ICP-Brasil')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(ModalSignIcpBrasil);
